import { cn } from "@/lib/utils";

/**
 * The brand-blue tick used on affirmative lists — plan cards, benefit lists,
 * the out-of-credits upsell.
 *
 * Lives here rather than beside the pricing page it started on: it is now used
 * by a dialog that can be raised from anywhere, and a globally-mounted modal
 * should not have to import a page module for an icon.
 *
 * Not lucide's `Check`, which several files import under the same name — this
 * one is deliberately heavier-stroked and brand-coloured.
 */
export const Check = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 16 16"
    aria-hidden
    className={cn("mt-0.5 size-4 shrink-0 text-primary-blue", className)}
  >
    <path
      d="M3.5 8.5 6.5 11.5 12.5 5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * The small inherit-colour tick, sized for a 15px checkbox or a chip. Same
 * shape as {@link Check} without the brand colour, top margin or fixed size,
 * so the caller decides all three.
 */
export const Tick = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 10 8"
    aria-hidden
    className={cn("size-2.5 shrink-0", className)}
  >
    <path
      d="M1 4L3.8 6.6L9 1.4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * The 15px checkbox square: the tick and its checked colours in one place,
 * so a settings row and a Done control cannot drift apart. The caller styles
 * the unchecked border for its own surface.
 */
export const CheckBox = ({
  checked,
  className,
}: {
  checked: boolean;
  className?: string;
}) => (
  <span
    className={cn(
      "flex size-[15px] shrink-0 items-center justify-center rounded border-[1.5px] transition-colors",
      checked
        ? "border-streamline-success bg-streamline-success"
        : "border-neutral-700",
      className,
    )}
  >
    {checked && <Tick className="text-streamline-on-success" />}
  </span>
);
